import { useEffect, useRef, useState, useCallback } from 'react';
import {
  Box,
  Card,
  Table,
  TableBody,
  Container,
  TableContainer,
  TablePagination,
  TableHead,
  TableRow,
  TableCell,
  IconButton,
  Grid,
  TextField,
  InputAdornment,
  Stack,
  Tooltip,
  Button,
  FormControlLabel,
  Checkbox,
  TableSortLabel,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  CircularProgress,
} from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import Page from '../../components/Page';
import Label from '../../components/Label';
import { useAppDispatch, useAppSelector } from '../../store';
import { findAdvertise } from '../../store/advertise/api';
import {
  setCurrentPage,
  setLimit,
  setSkip,
} from '../../store/advertise/advertiseSlice';
import Loader from '../../components/Loader';
import {
  setStatus as setAdvertiseStatus,
} from '../../store/advertise/advertiseSlice';
import { DateFormat, fDateLine, fTimeLine } from '../../utils/formatTime';
import { selectAdvertiseStatusColor } from '../../utils/selectAdvertiseStatusColor';
import { encryptString } from '../../utils/url';
import RemoveRedEyeIcon from '@mui/icons-material/RemoveRedEye';
import Iconify from '../../components/Iconify';
import InfoIcon from '@mui/icons-material/Info';
import {
  CSV_ADVERTISE_HEADERS,
  FILTER_ADVERTISE_STATUS,
} from '../../constants';
import { fileNameGeneraterWithoutType } from '../../utils/fileNameGenerater';
import { CSVLink } from 'react-csv';
import { isValidArray } from '../../utils/valid';
import { csv } from './CSV';
import { findAdvertiseExport } from '../../store/advertiseExport/api';
import { SortBy, AdvertiseStatus } from '../../enum';
import AutoComplete from '../../components/AutoComplete';
import { 
  setNavigatingWithinAdvertise, 
  getNavigatingWithinAdvertise, 
  setShouldResetFilters, 
  getShouldResetFilters,
  FILTER_STORAGE_KEY
} from './shared-state';
import ChangeAdvertiseStatusFormModal from './View/ChangeStatusModal';
import { bulkChangeAdvertiseStatus } from '../../store/changeAdvertiseStatus/api';
import useToggle from '../../hooks/useToggle';

// Custom hook for navigating to detail while preserving state
const useDetailNavigate = () => {
  const navigate = useNavigate();
  
  const goToDetail = (id: string | undefined) => {
    navigate(`/advertise/view/${encryptString(id ?? '')}`, {
      state: { fromListing: true }
    });
  };
  
  return goToDetail;
};

const Index = () => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const goToDetail = useDetailNavigate();
  const { data, totalRecords, limit, currentPage, skip, isLoading } =
    useAppSelector((state) => state.advertise);
  const [search, setSearch] = useState('');
  const [statusState, setStatusState] = useState('');
  const exportCsvElement = useRef<any>(null);
  const [csvStructureData, setCsvStructureData] = useState<any>([]);
  const [isDraftIncluded, setIsDraftIncluded] = useState<boolean>(false);
  const [sortBy, setSortBy] = useState(SortBy.updatedAt);
  const [sortByOrder, setSortByOrder] = useState('DESC');
  // const userRole = useAppSelector((state) => state.auth.user.userRole);
  const initialRender = useRef(true);
  const returnFromDetail = useRef(false);
  const [selectedAds, setSelectedAds] = useState<string[]>([]);
  const [openActionDialog, setOpenActionDialog] = useState(false);
  const [actionReason, setActionReason] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toggle: changeStatusModalOpen, onOpen: openChangeStatusModal, onClose: closeChangeStatusModal } = useToggle();
  const [bulkActionInProgress, setBulkActionInProgress] = useState(false);
  // New state for tracking filter loading
  const [isFilterLoading, setIsFilterLoading] = useState(false);
  const filtersApplied = useRef(false);
  const searchTimeoutRef = useRef<NodeJS.Timeout>();

  // Set the flag when navigating to detail
  const handleGoToDetail = (id: string | undefined) => {
    setNavigatingWithinAdvertise(true);
    setShouldResetFilters(false);
    goToDetail(id);
  };

  // Effect to handle component unmounting
  useEffect(() => {
    // This cleanup function runs when the component unmounts
    return () => {
      // Only clear filters if NOT navigating within the Advertise section
      if (!getNavigatingWithinAdvertise()) {
        localStorage.removeItem(FILTER_STORAGE_KEY);
      }
      // Reset the flag when unmounting
      setNavigatingWithinAdvertise(false);
    };
  }, []);

  // Load saved filters on initial render or when returning from detail
  useEffect(() => {
    // Check if we're returning from a detail page via history back
    if (location.key) {
      returnFromDetail.current = true;
    }

    if (initialRender.current || returnFromDetail.current) {
      setIsFilterLoading(true);
      
      const savedFilters = localStorage.getItem(FILTER_STORAGE_KEY);
      
      // Check if we should reset filters
      if (getShouldResetFilters() && !getNavigatingWithinAdvertise()) {
        // Clear saved filters
        localStorage.removeItem(FILTER_STORAGE_KEY);
        // Reset filter state
        setSearch('');
        setStatusState('');
        setIsDraftIncluded(false);
        setSortBy(SortBy.updatedAt);
        setSortByOrder('DESC');
        dispatch(setSkip({ skip: 0 }));
        dispatch(setCurrentPage({ currentPage: 0 }));
        dispatch(setLimit({ limit: 25 }));
        // Allow initial fetch to proceed with default filters
        setTimeout(() => {
          setIsFilterLoading(false);
          initialRender.current = false;
          returnFromDetail.current = false;
          // After loading/processing filters, set shouldResetFilters to true for next navigation
          setShouldResetFilters(true);
          // Trigger data fetch with default filters
          dispatch(
            findAdvertise({
              limit: 25,
              skip: 0,
              isDraftIncluded: false,
              sortByName: SortBy.updatedAt,
              sortByOrder: 'DESC',
            })
          );
        }, 100);
      } else if (savedFilters) {
        // Use saved filters if available and not resetting
        try {
        const filters = JSON.parse(savedFilters);
          
          // Store filter values to apply them after setting state
          const filterValues = {
            search: filters.search || '',
            statusState: filters.statusState || '',
            isDraftIncluded: filters.isDraftIncluded || false,
            sortBy: filters.sortBy || SortBy.updatedAt,
            sortByOrder: filters.sortByOrder || 'DESC',
            skip: filters.skip !== undefined ? filters.skip : 0,
            currentPage: filters.currentPage !== undefined ? filters.currentPage : 0,
            limit: filters.limit !== undefined ? filters.limit : 25
          };
          
          // Set UI state
          setSearch(filterValues.search);
          setStatusState(filterValues.statusState);
          setIsDraftIncluded(filterValues.isDraftIncluded);
          setSortBy(filterValues.sortBy);
          setSortByOrder(filterValues.sortByOrder);
          dispatch(setSkip({ skip: filterValues.skip }));
          dispatch(setCurrentPage({ currentPage: filterValues.currentPage }));
          dispatch(setLimit({ limit: filterValues.limit }));
          
          // Mark that we've applied filters from storage
          filtersApplied.current = true;
          
          // Ensure filters are applied with the API call after state is updated
          setTimeout(() => {
            setIsFilterLoading(false);
      initialRender.current = false;
      returnFromDetail.current = false;
            // After loading/processing filters, set shouldResetFilters to true for next navigation
            setShouldResetFilters(true);
            
            // Force data fetch with exactly these filter values to ensure synchronization
            dispatch(
              findAdvertise({
                limit: filterValues.limit,
                skip: filterValues.skip,
                ...(filterValues.search && { search: filterValues.search }),
                ...(filterValues.statusState && { status: filterValues.statusState }),
                isDraftIncluded: filterValues.isDraftIncluded,
                ...(filterValues.sortBy && { sortByName: filterValues.sortBy }),
                ...(filterValues.sortByOrder && { sortByOrder: filterValues.sortByOrder }),
              })
            );
          }, 100);
        } catch (error) {
          console.error("Error parsing saved filters:", error);
          // Reset to defaults if there's an error
          setSearch('');
          setStatusState('');
          setIsDraftIncluded(false);
          setSortBy(SortBy.updatedAt);
          setSortByOrder('DESC');
          dispatch(setSkip({ skip: 0 }));
          dispatch(setCurrentPage({ currentPage: 0 }));
          dispatch(setLimit({ limit: 25 }));
          
          // Allow initial fetch to proceed with default filters
          setTimeout(() => {
            setIsFilterLoading(false);
            initialRender.current = false;
            returnFromDetail.current = false;
      // After loading/processing filters, set shouldResetFilters to true for next navigation
      setShouldResetFilters(true);
            
            // Trigger data fetch with default filters
            dispatch(
              findAdvertise({
                limit: 25,
                skip: 0,
                isDraftIncluded: false,
                sortByName: SortBy.updatedAt,
                sortByOrder: 'DESC',
              })
            );
          }, 100);
        }
      } else {
        // No saved filters, proceed with default state
        setIsFilterLoading(false);
        initialRender.current = false;
        returnFromDetail.current = false;
        // After loading/processing filters, set shouldResetFilters to true for next navigation
        setShouldResetFilters(true);
      }
    }
  }, [location, dispatch]);

  // Save filters whenever they change
  useEffect(() => {
    if (!initialRender.current) {
      const filtersToSave = {
        search,
        statusState,
        isDraftIncluded,
        sortBy,
        sortByOrder,
        skip,
        currentPage,
        limit
      };
      localStorage.setItem(FILTER_STORAGE_KEY, JSON.stringify(filtersToSave));
    }
  }, [search, statusState, isDraftIncluded, sortBy, sortByOrder, skip, currentPage, limit]);

  // Handle API calls with error handling for network conditions
  useEffect(() => {
    // Only call this effect if not during initial render or returning from detail
    // This prevents duplicate API calls with the ones made in the filter loading effect
    if (!initialRender.current && !returnFromDetail.current) {
      // Set loading state
      setIsFilterLoading(true);
      
      // Create timeout for slow network detection
      const timeoutId = setTimeout(() => {
        console.log("Network request is taking longer than expected");
        // Keep loading indicator but don't change other state
      }, 5000); // 5 seconds timeout to indicate slow network
      
    dispatch(
      findAdvertise({
        limit,
        skip,
        ...(search && { search: search }),
        ...(statusState && { status: statusState }),
        isDraftIncluded: isDraftIncluded,
        ...(sortBy && { sortByName: sortBy }),
        ...(sortByOrder && { sortByOrder: sortByOrder }),
      })
      )
        .then(() => {
          // Success - clear timeout and loading state
          clearTimeout(timeoutId);
          setIsFilterLoading(false);
        })
        .catch((error) => {
          // Handle network errors
          console.error("Error fetching advertise data:", error);
          clearTimeout(timeoutId);
          setIsFilterLoading(false);
          
          // Could show a network error message here if needed
        });
    }
  }, [
    dispatch,
    limit,
    skip,
    statusState,
    isDraftIncluded,
    sortBy,
    sortByOrder,
    search,
  ]);

  useEffect(() => {
    // Only reset to first page when search changes, don't clear other filters
    if (!initialRender.current && search !== undefined) {
      dispatch(setSkip({ skip: 0 }));
      dispatch(setCurrentPage({ currentPage: 0 }));
    }
  }, [search, dispatch]);

  const handlePageChange = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent> | null,
    newPage: number
  ): void => {
    dispatch(setSkip({ skip: limit * newPage }));
    dispatch(setCurrentPage({ currentPage: newPage }));
    dispatch(setAdvertiseStatus({ status: 'idle' }));
  };
  const handleRowsPerPageChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ): void => {
    dispatch(setLimit({ limit: +event.target.value }));
  };

  const handleCsvExport = async () => {
    try {
      await dispatch(
        findAdvertiseExport({
          ...(search && { search: search }),
          ...(statusState && { status: statusState }),
          isDraftIncluded: isDraftIncluded,
          ...(sortBy && { sortByName: sortBy }),
          ...(sortByOrder && { sortByOrder: sortByOrder }),
          isExport: true,
          limit: 501,
          skip: 0, 
        })
      ).then((data: any) => {
        setCsvStructureData(csv(data?.payload?.data));
      });
    } catch (error) {}
  };

  useEffect(() => {
    if (
      isValidArray(csvStructureData) &&
      exportCsvElement &&
      exportCsvElement.current &&
      exportCsvElement.current.link
    ) {
      exportCsvElement.current.link.click();
      setCsvStructureData([]);
    }
  }, [isValidArray(csvStructureData)]);

  const handleRequestSort = (property: any) => {
    const isAsc = sortBy === property && sortByOrder === 'ASC';
    setSortByOrder(isAsc ? 'DESC' : 'ASC');
    setSortBy(property);
  };

  // Check if we should show multi-select checkboxes
  const shouldShowCheckboxes = statusState === AdvertiseStatus.submit || statusState === AdvertiseStatus.published;

  // Handle multiple selection
  const handleSelectAd = (id: string) => {
    setSelectedAds((prev) => {
      if (prev.includes(id)) {
        return prev.filter((adId) => adId !== id);
      } else {
        return [...prev, id];
      }
    });
  };

  // Handle select all ads that are submitted or published on current page
  const handleSelectAllAds = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const eligibleIds = data?.map((ad) => ad.id) || [];
      setSelectedAds(eligibleIds);
    } else {
      setSelectedAds([]);
    }
  };

  // For bulk suspend action
  const handleOpenActionDialog = () => {
    setActionReason('');
    setOpenActionDialog(true);
  };

  const handleCloseActionDialog = () => {
    setOpenActionDialog(false);
    setActionReason('');
  };

  const handleActionSubmit = async () => {
    setIsSubmitting(true);
    try {
      await performBulkSuspend(actionReason);
      handleCloseActionDialog();
      
      // Force refresh data after suspension
      setTimeout(() => {
        refreshData();
      }, 500);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle bulk actions
  const performBulkSuspend = async (reason: string) => {
    if (!reason.trim()) return;
    
    setBulkActionInProgress(true);
    try {
      // Use bulk API endpoint for suspending multiple ads at once
      await dispatch(bulkChangeAdvertiseStatus({
        advertiseIds: selectedAds,
        status: AdvertiseStatus.suspended,
        rejectionReason: reason,
      })).unwrap();

      // Refresh data and clear selections
      dispatch(setAdvertiseStatus({ status: 'idle' }));
      dispatch(findAdvertise({
        limit,
        skip,
        ...(search && { search: search }),
        ...(statusState && { status: statusState }),
        isDraftIncluded: isDraftIncluded,
        ...(sortBy && { sortByName: sortBy }),
        ...(sortByOrder && { sortByOrder: sortByOrder }),
      }));
      
      setSelectedAds([]);
    } catch (error: any) {
      console.error('Error suspending advertisements:', error);
      // Instead of letting the global error handler show the popup,
      // we'll handle it locally and silently
      
      // Still attempt to refresh the data to keep UI in sync
      setTimeout(() => {
        refreshData();
      }, 500);
      
      setSelectedAds([]);
    } finally {
      setBulkActionInProgress(false);
    }
  };
  
  // Function to refresh the listing data
  const refreshData = () => {
    console.log("Refreshing advertise listing data");
    dispatch(setAdvertiseStatus({ status: 'idle' }));
    
    // Fetch fresh data with current filters
    setIsFilterLoading(true);
    
    // Create timeout for slow network detection
    const timeoutId = setTimeout(() => {
      console.log("Data refresh is taking longer than expected");
      // Keep loading indicator but don't change other state
    }, 5000); // 5 seconds timeout
    
    dispatch(findAdvertise({
      limit,
      skip,
      ...(search && { search: search }),
      ...(statusState && { status: statusState }),
      isDraftIncluded: isDraftIncluded,
      ...(sortBy && { sortByName: sortBy }),
      ...(sortByOrder && { sortByOrder: sortByOrder }),
    }))
      .then(() => {
        // Success - clear timeout and loading state
        clearTimeout(timeoutId);
        setIsFilterLoading(false);
      })
      .catch((error) => {
        // Handle network errors
        console.error("Error refreshing advertise data:", error);
        clearTimeout(timeoutId);
        setIsFilterLoading(false);
        
        // Could show a network error message here if needed
      });
  };

  // Function to get selected ad data for bulk action
  const getSelectedAdData = () => {
    // Get first selected ad for reference values
    const firstSelectedAd = data?.find(ad => selectedAds.includes(ad.id));
    
    console.log("Getting selected ad data:", {
      count: selectedAds.length,
      ids: selectedAds,
      currentStatus: statusState
    });
    
    return {
      id: selectedAds.length === 1 ? firstSelectedAd?.id || '' : '',
      status: statusState, // Use the current filter status
      isBulkAction: true,  // Always treat as bulk action when coming from listing
      selectedIds: selectedAds
    };
  };

  // Clear selections when filters change or when multi-select is disabled
  useEffect(() => {
    setSelectedAds([]);
  }, [search, statusState, isDraftIncluded, sortBy, sortByOrder]);

  // Debounced search handler
  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearch(value);
    
    // Clear any existing timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    // Set new timeout for search
    searchTimeoutRef.current = setTimeout(() => {
      // Reset to first page when search changes
      dispatch(setSkip({ skip: 0 }));
      dispatch(setCurrentPage({ currentPage: 0 }));
    }, 500); // 500ms debounce
  }, [dispatch]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  return (
    <Page title={'Advertise: List'}>
      <Container maxWidth={'xl'}>
        {/* Suspend Dialog for published ads */}
        <Dialog 
          open={openActionDialog} 
          onClose={handleCloseActionDialog}
          fullWidth
          maxWidth="sm"
        >
          <DialogTitle>Suspension Reason</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Please provide a reason for suspending the selected advertisements.
            </DialogContentText>
            <TextField
              autoFocus
              margin="dense"
              label="Reason"
              type="text"
              fullWidth
              variant="outlined"
              multiline
              rows={4}
              value={actionReason}
              onChange={(e) => setActionReason(e.target.value)}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseActionDialog}>Cancel</Button>
            <Button 
              onClick={handleActionSubmit} 
              variant="contained" 
              disabled={!actionReason.trim() || isSubmitting}
            >
              {isSubmitting ? 'Processing...' : 'Submit'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Change Status Modal for submitted ads */}
        {changeStatusModalOpen && (
          <ChangeAdvertiseStatusFormModal
            toggle={changeStatusModalOpen}
            onClose={() => {
              setBulkActionInProgress(false);
              closeChangeStatusModal();
            }}
            advertise={getSelectedAdData()}
            setAdvertise={() => {
              // Set flag to indicate action in progress
              setBulkActionInProgress(true);
              
              // Refresh data after status change with a longer timeout to ensure API completes
              console.log("Status change detected, refreshing data");
              setTimeout(() => {
                // Force a refresh of the data
                dispatch(setAdvertiseStatus({ status: 'idle' }));
                dispatch(findAdvertise({
                  limit,
                  skip,
                  ...(search && { search: search }),
                  ...(statusState && { status: statusState }),
                  isDraftIncluded: isDraftIncluded,
                  ...(sortBy && { sortByName: sortBy }),
                  ...(sortByOrder && { sortByOrder: sortByOrder }),
                }));
                
                // Clear selected ads and reset flag
                setSelectedAds([]);
                setBulkActionInProgress(false);
              }, 1000);  // Increase timeout to 1000ms (1 second)
            }}
          />
        )}

        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'stretch', sm: 'center' }}
          justifyContent='space-between'
          spacing={{ xs: 1.5, sm: 1.5 }}
          my={1}
        >
          <Box>
            {shouldShowCheckboxes && selectedAds.length > 0 && (
              <>
                {statusState === AdvertiseStatus.submit && (
                  <Button
                    variant='contained'
                    startIcon={<Iconify icon={'eva:edit-outline'} />}
                    onClick={openChangeStatusModal}
                    disabled={bulkActionInProgress}
                  >
                    Change Status
                  </Button>
                )}
                {statusState === AdvertiseStatus.published && (
                  <Button
                    variant='contained'
                    startIcon={<Iconify icon={'eva:edit-outline'} />}
                    onClick={handleOpenActionDialog}
                    disabled={bulkActionInProgress}
                  >
                    Suspend Advertise
                  </Button>
                )}
              </>
            )}
          </Box>
          <Stack
            direction='row'
            alignItems='center'
            spacing={{ xs: 0.5, sm: 1.5 }}
        >
          {/* {userRole !== 'adminTier3' && (
            <Button
              variant='contained'
              component={RouterLink}
              to={'/advertise/create'}
              startIcon={<Iconify icon={'eva:plus-fill'} />}
            >
              {'New Advertise'}
            </Button>
          )} */}
          <CSVLink
            ref={exportCsvElement}
            headers={CSV_ADVERTISE_HEADERS}
            data={csvStructureData}
            filename={fileNameGeneraterWithoutType('Advertise', 'csv')}
            style={{ textDecoration: 'none' }}
            asyncOnClick={true}
          />
          <Tooltip title={'Export to download'}>
            <Button
              variant='contained'
              startIcon={<i className='fa-solid fa-file-csv'></i>}
              onClick={handleCsvExport}
            >
              {'Export CSV'}
            </Button>
          </Tooltip>
          </Stack>
        </Stack>
        <Card sx={{ mt: 1.5 }}>
          <Grid
            container
            spacing={2}
            sx={{ py: 2.5, px: 3 }}
          >
            <Grid
              item
              xs={12}
              sm={5}
            >
              <TextField
                fullWidth
                value={search}
                onChange={handleSearchChange}
                placeholder={'Search...'}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position='start'>
                      <Iconify
                        icon={'eva:search-fill'}
                        sx={{ color: 'text.disabled', width: 20, height: 20 }}
                      />
                    </InputAdornment>
                  ),
                  endAdornment: isFilterLoading && (
                    <InputAdornment position='end'>
                      <CircularProgress size={20} />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid
              item
              xs={12}
              sm={5}
            >
              <AutoComplete
                onChange={(event, newValue) => {
                  if (typeof newValue === 'string') {
                  } else if (newValue) {
                    setStatusState(newValue.value);
                  }
                }}
                onInputChange={(event, newInputValue) => {
                  if (!newInputValue) {
                    setStatusState('');
                  }
                }}
                renderOption={(props, option) => (
                  <li {...props}>{option.name}</li>
                )}
                getOptionLabel={(option) => option.name}
                fetchFunction={() => null}
                label={'Select Status'}
                loading={isFilterLoading}
                options={FILTER_ADVERTISE_STATUS}
                searchFunction={() => null}
                value={
                  FILTER_ADVERTISE_STATUS?.filter(
                    (status) => status.value === statusState
                  )?.[0] || null
                }
                disabled={isFilterLoading}
              />
            </Grid>
            <Grid
              item
              xs={12}
              sm={2}
              display={'flex'}
              sx={{
                justifyContent: { xs: 'flex-start', sm: 'center' },
              }}
            >
              <FormControlLabel
                control={
                  <Checkbox
                    color='primary'
                    name='activeState'
                    onChange={(e) => {
                      setIsDraftIncluded(e.target.checked);
                    }}
                    checked={isDraftIncluded}
                    disabled={isFilterLoading}
                  />
                }
                label={'Draft Included'}
              />
            </Grid>
          </Grid>
          <TableContainer sx={{ width: '100%', overflowX: 'auto', position: 'relative' }}>
            <Table
              size={'medium'}
              sx={{
                minWidth: 1000,
                borderCollapse: 'separate',
                borderSpacing: '0 10px',
              }}
            >
                <TableHead
                  sx={{
                    background: '#f2d2df',
                  }}
                >
                  <TableRow>
                    {shouldShowCheckboxes && (
                      <TableCell padding="checkbox">
                        <Checkbox
                          indeterminate={
                            selectedAds.length > 0 && 
                            selectedAds.length < data?.length
                          }
                          checked={
                            data?.length > 0 && 
                            selectedAds.length === data?.length
                          }
                          onChange={handleSelectAllAds}
                        />
                      </TableCell>
                    )}
                    <TableCell align='center'>
                      <TableSortLabel
                        active={sortBy === SortBy.advertiseNumber}
                        direction={sortByOrder === 'ASC' ? 'asc' : 'desc'}
                        onClick={() =>
                          handleRequestSort(SortBy.advertiseNumber)
                        }
                      >
                        <div style={{ color: '#64013c' }}>{'Ad No.'}</div>
                      </TableSortLabel>
                    </TableCell>
                    <TableCell align='center'>
                      <TableSortLabel
                        active={sortBy === SortBy.userName}
                        direction={sortByOrder === 'ASC' ? 'asc' : 'desc'}
                        onClick={() => handleRequestSort(SortBy.userName)}
                      >
                        <div style={{ color: '#64013c' }}>{'User'}</div>
                      </TableSortLabel>
                    </TableCell>
                    <TableCell align='center'>
                      <TableSortLabel
                        active={sortBy === SortBy.title}
                        direction={sortByOrder === 'ASC' ? 'asc' : 'desc'}
                        onClick={() => handleRequestSort(SortBy.title)}
                      >
                        <div style={{ color: '#64013c' }}>{'Title'}</div>
                      </TableSortLabel>
                    </TableCell>
                    <TableCell align='center'>
                      <TableSortLabel
                        active={sortBy === SortBy.price}
                        direction={sortByOrder === 'ASC' ? 'asc' : 'desc'}
                        onClick={() => handleRequestSort(SortBy.price)}
                      >
                        <div style={{ color: '#64013c' }}>{'Price'}</div>
                      </TableSortLabel>
                    </TableCell>
                    <TableCell align='center'>
                      <TableSortLabel
                        active={sortBy === SortBy.startDate}
                        direction={sortByOrder === 'ASC' ? 'asc' : 'desc'}
                        onClick={() => handleRequestSort(SortBy.startDate)}
                      >
                        <div style={{ color: '#64013c' }}>{'Start Date'}</div>
                      </TableSortLabel>
                    </TableCell>
                    <TableCell align='center'>
                      <TableSortLabel
                        active={sortBy === SortBy.endDate}
                        direction={sortByOrder === 'ASC' ? 'asc' : 'desc'}
                        onClick={() => handleRequestSort(SortBy.endDate)}
                      >
                        <div style={{ color: '#64013c' }}>{'End Date'}</div>
                      </TableSortLabel>
                    </TableCell>
                    <TableCell align='center'>
                      <TableSortLabel
                        active={sortBy === SortBy.status}
                        direction={sortByOrder === 'ASC' ? 'asc' : 'desc'}
                        onClick={() => handleRequestSort(SortBy.status)}
                      >
                        <div style={{ color: '#64013c' }}>{'Status'}</div>
                      </TableSortLabel>
                    </TableCell>
                    <TableCell align='center'>
                      <TableSortLabel
                        active={sortBy === SortBy.updatedAt}
                        direction={sortByOrder === 'ASC' ? 'asc' : 'desc'}
                        onClick={() => handleRequestSort(SortBy.updatedAt)}
                      >
                        <div style={{ color: '#64013c' }}>
                          <div style={{ whiteSpace: 'nowrap' }}>{'Updated Date'}</div>
                          <div style={{ whiteSpace: 'nowrap' }}>{'Time'}</div>
                        </div>
                      </TableSortLabel>
                    </TableCell>
                    <TableCell align='center'>{'Action'}</TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {isLoading || isFilterLoading ? (
                    <TableRow hover>
                      <TableCell
                        colSpan={shouldShowCheckboxes ? 10 : 9}
                        align='center'
                      >
                        <Loader />
                      </TableCell>
                    </TableRow>
                  ) : data?.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={shouldShowCheckboxes ? 10 : 9}
                        align='center'
                      >
                        {'No record found'}
                      </TableCell>
                    </TableRow>
                  ) : (
                    data?.map((advertise) => (
                      <TableRow key={advertise.id}>
                        {shouldShowCheckboxes && (
                          <TableCell padding="checkbox">
                            <Checkbox
                              checked={selectedAds.includes(advertise.id)}
                              onChange={() => handleSelectAd(advertise.id)}
                            />
                          </TableCell>
                        )}
                        <TableCell align='center'>
                          {advertise?.advertiseNumber}
                        </TableCell>
                        <TableCell align='center'>
                          {advertise?.createdByUser?.name}
                        </TableCell>
                        <TableCell align='center'>
                          <Tooltip title={advertise?.title || ''}>
                            <Box sx={{
                              maxWidth: '250px',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              margin: '0 auto'
                            }}>
                              {advertise?.title}
                            </Box>
                          </Tooltip>
                        </TableCell>
                        <TableCell align='center'>{advertise?.price}</TableCell>
                        <TableCell align='center'>
                          {advertise?.startDate
                            ? DateFormat(advertise?.startDate)
                            : '-'}
                        </TableCell>
                        <TableCell align='center'>
                          {advertise?.endDate
                            ? DateFormat(advertise?.endDate)
                            : '-'}
                        </TableCell>
                        <TableCell align='center'>
                          <Label
                            variant='ghost'
                            color={selectAdvertiseStatusColor(
                              advertise?.status
                            )}
                          >
                            {advertise?.status}
                            {(advertise?.status === AdvertiseStatus.suspended || 
                              advertise?.status === AdvertiseStatus.rejected) && (
                              <Tooltip 
                                title={advertise?.rejectionReason || 'No reason provided'} 
                                placement="top"
                                arrow
                              >
                                <IconButton
                                  size="small"
                                  sx={{ ml: 1 }}
                                >
                                  <InfoIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Label>
                        </TableCell>
                        <TableCell align='center'>
                          {(advertise?.updatedAt || advertise?.createdAt) ? (
                            <Box sx={{ whiteSpace: 'nowrap' }}>
                              <Box component="span" sx={{ display: 'block' }}>
                                {fDateLine(advertise.updatedAt || advertise.createdAt)}
                              </Box>
                              <Box component="span" sx={{ display: 'block' }}>
                                {fTimeLine(advertise.updatedAt || advertise.createdAt)}
                              </Box>
                            </Box>
                          ) : '-'}
                        </TableCell>
                        <TableCell align='center'>
                          <IconButton 
                            aria-label='visibility'
                            onClick={() => handleGoToDetail(advertise?.id)}
                          >
                            <RemoveRedEyeIcon style={{ color: '#808080' }} />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

          <Box sx={{ position: 'relative' }}>
            <TablePagination
              rowsPerPageOptions={[25, 50, 100]}
              component='div'
              count={totalRecords}
              rowsPerPage={limit}
              page={Math.min(Math.round(totalRecords / limit), currentPage)}
              onPageChange={handlePageChange}
              onRowsPerPageChange={handleRowsPerPageChange}
              disabled={isLoading || isFilterLoading}
            />
          </Box>
        </Card>
      </Container>
    </Page>
  );
};

export default Index;
